import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";

const links = [
  { id: "work", label: "Work" },
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "experience", label: "Experience" },
  { id: "contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [studioName, setStudioName] = useState("Studio/Saar");
  const [availableText, setAvailableText] = useState("Available for work");
  const [available, setAvailable] = useState(true);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api.get("/footer").then((r) => r.data?.studio_name && setStudioName(r.data.studio_name)).catch(() => {});
    api.get("/hero").then((r) => {
      if (r.data?.available_text) setAvailableText(r.data.available_text);
      if (typeof r.data?.available_for_work === "boolean") setAvailable(r.data.available_for_work);
    }).catch(() => {});
  }, []);

  const goTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "backdrop-blur-xl bg-[#F4F4F0]/70 border-b border-neutral-200" : ""
        }`}
        data-testid="site-header"
      >
        <div className="px-6 md:px-12 lg:px-24 py-5 flex items-center justify-between">
          <a
            href="#top"
            onClick={(e) => { e.preventDefault(); goTo("top"); }}
            className="font-display font-black text-lg tracking-tight uppercase"
            data-testid="logo-link"
          >
            {studioName}<span className="text-[#FF3E00]">®</span>
          </a>

          <nav className="hidden lg:flex items-center gap-10">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => goTo(l.id)}
                className="font-body text-sm uppercase tracking-[0.2em] link-line"
                data-testid={`nav-${l.id}-link`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <button
              onClick={() => goTo("contact")}
              className="hidden lg:inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.2em]"
              data-testid="header-cta-button"
            >
              <span
                className={`w-2 h-2 rounded-full ${available ? "bg-[#FF3E00] animate-pulse" : "bg-neutral-400"}`}
                data-testid="header-availability-dot"
              />
              {available ? availableText : "Not available"}
            </button>
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden font-display font-bold text-sm uppercase tracking-widest"
              data-testid="menu-toggle-button"
              aria-label="Toggle menu"
            >
              {open ? "Close" : "Menu"}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-40 bg-[#0A0A0A] text-[#F4F4F0] lg:hidden flex flex-col"
            data-testid="mobile-menu-panel"
          >
            <div className="flex-1 flex flex-col justify-center px-8 gap-6">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                  onClick={() => goTo(l.id)}
                  className="font-display font-black text-5xl text-left uppercase tracking-tighter"
                  data-testid={`mobile-nav-${l.id}-link`}
                >
                  {l.label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
